
import * as React from "react"
import { motion } from "framer-motion"

import { cn } from "@/lib/utils"

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'glass' | 'modern' | 'elevated' | 'floating' | 'gradient' | 'minimal'
    animated?: boolean
    hover?: boolean
  }
>(({ className, variant = 'default', animated = false, hover = false, ...props }, ref) => {
  const variants = {
    default: "rounded-lg border bg-card text-card-foreground shadow-sm",
    glass: "rounded-modern border border-white/20 bg-white/10 backdrop-blur-glass text-card-foreground shadow-glass",
    modern: "rounded-modern border-0 bg-card text-card-foreground shadow-card hover:shadow-card-hover transition-all duration-300",
    elevated: "rounded-modern border-0 bg-card text-card-foreground shadow-modern hover:shadow-modern-lg hover:-translate-y-1 transition-all duration-300",
    floating: "rounded-modern border border-border/10 bg-gradient-to-br from-card/95 to-card/85 backdrop-blur-sm text-card-foreground shadow-modern hover:shadow-modern-lg hover:-translate-y-2 transition-all duration-500",
    gradient: "rounded-modern border-0 bg-gradient-to-br from-card via-card/95 to-muted/20 text-card-foreground shadow-modern hover:shadow-modern-lg transition-all duration-300",
    minimal: "rounded-modern border border-border/5 bg-card/50 backdrop-blur-sm text-card-foreground hover:bg-card/80 transition-all duration-300"
  }

  const cardClasses = cn(variants[variant], className)

  if (animated) {
    // Extract HTML event handlers that conflict with Framer Motion
    const { 
      onDrag, 
      onDragStart, 
      onDragEnd,
      onAnimationStart,
      onAnimationEnd,
      onAnimationIteration,
      onTransitionEnd,
      ...motionProps 
    } = props
    
    return (
      <motion.div
        ref={ref}
        className={cardClasses}
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          type: "spring", 
          stiffness: 100, 
          damping: 15,
          duration: 0.6 
        }}
        whileHover={hover ? { 
          y: -5, 
          scale: 1.02,
          transition: { duration: 0.2 }
        } : undefined}
        {...motionProps}
      />
    )
  }

  return (
    <div
      ref={ref}
      className={cardClasses}
      {...props}
    />
  )
})
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    animated?: boolean
  }
>(({ className, animated = false, ...props }, ref) => {
  const headerClasses = cn("flex flex-col space-y-1.5 p-6", className)

  if (animated) {
    // Extract HTML event handlers that conflict with Framer Motion
    const { 
      onDrag, 
      onDragStart, 
      onDragEnd,
      onAnimationStart,
      onAnimationEnd,
      onAnimationIteration,
      onTransitionEnd,
      ...motionProps 
    } = props
    
    return (
      <motion.div
        ref={ref}
        className={headerClasses}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.3 }}
        {...motionProps}
      />
    )
  }

  return (
    <div
      ref={ref}
      className={headerClasses}
      {...props}
    />
  )
})
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement> & {
    animated?: boolean
  }
>(({ className, animated = false, ...props }, ref) => {
  const titleClasses = cn(
    "text-2xl font-semibold leading-none tracking-tight",
    className
  )

  if (animated) {
    // Extract HTML event handlers that conflict with Framer Motion
    const { 
      onDrag, 
      onDragStart, 
      onDragEnd,
      onAnimationStart,
      onAnimationEnd,
      onAnimationIteration,
      onTransitionEnd,
      ...motionProps 
    } = props
    
    return (
      <motion.h3
        ref={ref}
        className={titleClasses}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2, duration: 0.3 }}
        {...motionProps}
      />
    )
  }

  return (
    <h3
      ref={ref}
      className={titleClasses}
      {...props}
    />
  )
})
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement> & {
    animated?: boolean
  }
>(({ className, animated = false, ...props }, ref) => {
  const descriptionClasses = cn("text-sm text-muted-foreground", className)

  if (animated) {
    // Extract HTML event handlers that conflict with Framer Motion
    const { 
      onDrag, 
      onDragStart, 
      onDragEnd,
      onAnimationStart,
      onAnimationEnd,
      onAnimationIteration,
      onTransitionEnd,
      ...motionProps 
    } = props
    
    return (
      <motion.p
        ref={ref}
        className={descriptionClasses}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.3 }}
        {...motionProps}
      />
    )
  }

  return (
    <p
      ref={ref}
      className={descriptionClasses}
      {...props}
    />
  )
})
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    animated?: boolean
  }
>(({ className, animated = false, ...props }, ref) => {
  const contentClasses = cn("p-6 pt-0", className)

  if (animated) {
    // Extract HTML event handlers that conflict with Framer Motion
    const { 
      onDrag, 
      onDragStart, 
      onDragEnd,
      onAnimationStart,
      onAnimationEnd,
      onAnimationIteration,
      onTransitionEnd,
      ...motionProps 
    } = props
    
    return (
      <motion.div 
        ref={ref} 
        className={contentClasses}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.3 }}
        {...motionProps} 
      />
    )
  }

  return (
    <div ref={ref} className={contentClasses} {...props} />
  )
})
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    animated?: boolean
  }
>(({ className, animated = false, ...props }, ref) => {
  const footerClasses = cn("flex items-center p-6 pt-0", className)

  if (animated) {
    // Extract HTML event handlers that conflict with Framer Motion
    const { 
      onDrag, 
      onDragStart, 
      onDragEnd,
      onAnimationStart,
      onAnimationEnd,
      onAnimationIteration,
      onTransitionEnd,
      ...motionProps 
    } = props
    
    return (
      <motion.div
        ref={ref}
        className={footerClasses}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5, duration: 0.3 }}
        {...motionProps}
      />
    )
  }

  return (
    <div
      ref={ref}
      className={footerClasses}
      {...props}
    />
  )
})
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
